import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { UserMetadataService } from '@core/services/user-metadata.service';
import {
  DvDropdownGridElement,
  DvDynamicFormElement,
} from '@devinco/ui/legacy';
import {
  DvButtonElement,
  DvCheckboxElement,
  DvCountryDropdownElement,
  DvNumberboxElement,
  DvSpaceElement,
  DvTextareaElement,
  DvTextboxElement,
} from '@devinco/ui/legacy';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { DropdownGridOptionsService } from '@features/vt-grid/grid-options/dropdown-grid-options.service';
import { TranslateService } from '@ngx-translate/core';
import { POIType } from '@pages/poi-page/interfaces';
import RouteNetworkColorUtils from '@pages/route-network-page/utils/route-network-color.utils';
import { Entity } from '@shared/constants/entity';
import { ICellRendererParams } from 'ag-grid-community';

@Injectable()
export class POIInformationVariablesTabService {
  private metaDataService = inject(UserMetadataService);
  private dropdownGridOptionsService = inject(DropdownGridOptionsService);
  private translate = inject(TranslateService);

  readonly routeNetworkElements: DvDynamicFormElement[] =
    this.metaDataService.hasSkiDomain()
      ? [
          new DvCheckboxElement({
            colspan: 12,
            key: 'IsPublic',
            checkboxLabel: 'POI.IsPublic',
          }),
        ]
      : [];

  readonly acceptanceRadiusElements: DvDynamicFormElement[] = [
    new DvTextboxElement({
      colspan: 6,
      key: 'AcceptanceRadius',
      label: 'POI.AcceptanceRadius',
      validators: this.metaDataService.hasSkiDomain()
        ? [Validators.min(25)]
        : [Validators.required, Validators.min(25)],
    }),
    new DvSpaceElement({
      colspan: 6,
    }),
  ];

  readonly activeElement: DvDynamicFormElement[] = [
    new DvCheckboxElement({
      colspan: 12,
      key: 'Active',
      checkboxLabel: 'POI.Active',
    }),
  ];

  getPurposeElements = (
    generateLinkClickFn?: () => void
  ): DvDynamicFormElement[] => [
    new DvTextareaElement({
      colspan: 12,
      rowspan: 2,
      key: 'Purpose',
      label: 'POI.Purpose',
    }),
    new DvButtonElement({
      key: 'generateLink',
      extra: { ignoreInRequest: true },
      text: 'POI.GenerateWebLink',
      clickFn: generateLinkClickFn,
      hidden: !this.metaDataService.hasSkiDomain(),
      color: 'primary',
    }),
    new DvCheckboxElement({
      colspan: 12,
      key: 'Personal',
      checkboxLabel: 'POI.Private',
    }),
  ];

  getLinkGenerationElements = (): DvDynamicFormElement[] => [
    new DvTextboxElement({
      key: 'link',
      label: 'POI.GenerateLinkDialog.Link',
    }),
    new DvTextboxElement({
      key: 'displayText',
      label: 'POI.GenerateLinkDialog.DisplayText',
    }),
  ];

  details1Elements(addressSearchClickFn: () => void): DvDynamicFormElement[] {
    return [
      new DvTextareaElement({
        colspan: 12,
        rowspan: 2,
        key: 'Description',
        label: 'POI.Information.Description',
      }),
      new DvDropdownGridElement({
        key: 'POITypeID',
        valueField: 'POITypeID',
        labelFields: ['TranslationKey'],
        labelFormatter: event =>
          this.translate.instant(event.params.selectedValue.TranslationKey),
        label: 'POI.POIType',
        hidden: !this.metaDataService.hasSkiDomain(),
        colspan: 12,
        enableColumnHeaders: true,
        gridOptions: {
          ...this.dropdownGridOptionsService.clientSideSingleSelect(
            Entity.POIType,
            [
              { field: 'POITypeID', type: ColumnType.Id, hide: true },
              {
                field: 'IconKey',
                width: 30,
                headerName: '',
                cellRenderer: (params: ICellRendererParams<POIType>) => {
                  if (!params.value) return '';

                  return `${RouteNetworkColorUtils.getIconWithFill(
                    params.data?.IconKey || 'icon-info',
                    params.data?.IconColor || 'black'
                  ).replace(
                    'width="48" height="48"',
                    'width="32" height="32"'
                  )}`;
                },
              },
              {
                field: 'TranslationKey',
                type: ColumnType.TranslatedText,
                headerName: 'POIType.Name',
                width: 130,
              },
              {
                field: 'Severity_TranslationKey',
                type: ColumnType.TranslatedText,
                headerName: 'POIType.Severity',
                width: 90,
              },
              {
                field: 'Description_TranslationKey',
                type: ColumnType.TranslatedText,
                headerName: 'POIType.Description',
              },
              { field: 'IconColor', type: ColumnType.Invisible },
            ].map(colDef => ({
              ...colDef,
              headerName: colDef.headerName
                ? this.translate.instant(colDef.headerName)
                : '',
            }))
          ),
          getRowId: ({ data }) => String(data.POITypeID),
        },
      }),
      new DvTextboxElement({
        colspan: 6,
        key: 'Address',
        label: 'POI.Address',
      }),
      new DvButtonElement({
        key: 'addressSearchBtn',
        extra: { ignoreInRequest: true },
        colspan: 6,
        icon: 'library',
        text: 'POI.AddressSearch',
        clickFn: addressSearchClickFn,
      }),
    ];
  }

  details2Elements = (
    coordinatesSearchFn: () => void
  ): DvDynamicFormElement[] => {
    return [
      new DvTextboxElement({
        colspan: 6,
        key: 'PostalNumber',
        label: 'POI.PostalNumber',
        disabled: true,
      }),
      new DvTextboxElement({
        colspan: 6,
        key: 'PostalPlace',
        label: 'POI.PostalPlace',
      }),
      new DvCountryDropdownElement({
        colspan: 6,
        key: 'CountryCode',
        label: 'POI.Country',
        flagDirectory: 'assets/flags/',
      }),
      new DvSpaceElement({
        colspan: 6,
      }),
      new DvNumberboxElement({
        colspan: 3,
        key: 'GPSLat',
        label: 'POI.Lat',
        validators: [Validators.required],
        fieldMask: {
          decimalDigits: 5,
          allowNegativeNumbers: false,
          thousandSeparator: '',
        },
      }),
      new DvNumberboxElement({
        colspan: 3,
        key: 'GPSLon',
        label: 'POI.Lon',
        validators: [Validators.required],
        fieldMask: {
          decimalDigits: 5,
          allowNegativeNumbers: false,
          thousandSeparator: '',
        },
      }),
      new DvButtonElement({
        key: 'coordinatesSearchBtn',
        extra: { ignoreInRequest: true },
        colspan: 6,
        icon: 'library',
        text: 'POI.CoordinatesSearch',
        clickFn: coordinatesSearchFn,
      }),
      new DvTextboxElement({ key: 'IconColor', hidden: true }),
      new DvTextboxElement({ key: 'IconKey', hidden: true }),
      new DvTextboxElement({ key: 'POIType_TranslationKey', hidden: true }),
      new DvTextboxElement({ key: 'SeverityID', hidden: true }),
      new DvTextboxElement({ key: 'DomainID', hidden: true }),
      new DvTextboxElement({ key: 'DomainName', hidden: true }),
      new DvTextboxElement({ key: 'Domain_TranslationKey', hidden: true }),
      new DvTextboxElement({ key: 'SeverityName', hidden: true }),
      new DvTextboxElement({ key: 'Severity_TranslationKey', hidden: true }),
    ];
  };
}
